/**
 * Hit Discovery Shortcut — fragment-based hit discovery (FBDD).
 *
 * The REAL hit discovery feature: produces NEW compounds that didn't
 * exist in the workspace before, via BMaps's fragment growing pipeline.
 *
 * Pipeline: grow-from-all-atoms → energy-minimize → predict-gnina-scores
 *
 * Distinct from dock-and-score (which evaluates EXISTING compounds)
 * and from the fragment grow shortcut (which only grows, doesn't
 * minimize or score).
 *
 * Requires a fragment map — only works for proteins that have
 * pre-computed Boltzmann Maps fragment simulation data.
 *
 * @module gibbs/knowledge/hitDiscoveryShortcut
 */

// ─── Phrase detection ──────────────────────────────────────────────────────

// Keep TIGHT — canonical phrases only. The LLM handles fuzzy variants.
const HIT_DISCOVERY_PHRASES = [
    'discover hits',
    'discovery hits',
    'hit discovery',
    'find hits',
    'find new compounds',
    'screen fragments',
    'fragment screen',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchHitDiscoveryIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return HIT_DISCOVERY_PHRASES.some((p) => lower.includes(p));
}

// ─── Precondition check ────────────────────────────────────────────────────

/**
 * Check workspace preconditions for fragment-based hit discovery.
 *
 * @param {Object} workspace - App.Workspace
 * @returns {{ready: boolean, message?: string}}
 */
export function checkHitDiscoveryPreconditions(workspace) {
    if (!workspace) {
        return { ready: false, message: 'No workspace available.' };
    }

    // Protein loaded?
    if (typeof workspace.firstCaseData === 'function') {
        const cd = workspace.firstCaseData();
        if (!cd || (typeof cd.getSolute === 'function' && !cd.getSolute())) {
            return {
                ready: false,
                message: 'I understand you want to run **hit discovery**. '
                    + 'However, I need a protein loaded first. '
                    + 'Load a protein, then ask me again.',
            };
        }
    }

    // Fragment map available?
    if (typeof workspace.firstCaseData === 'function') {
        const cd = workspace.firstCaseData();
        if (cd) {
            const fragInfo = cd.getAvailableFragmentInfo?.();
            const hasFrags = fragInfo
                && typeof fragInfo.items === 'function'
                && fragInfo.items().length > 0;
            if (!hasFrags) {
                return {
                    ready: false,
                    message: 'I understand you want to run **hit discovery**. '
                        + 'However, fragment-based hit discovery requires '
                        + 'a **fragment map** (pre-computed Boltzmann Maps '
                        + 'simulation data), and this protein doesn\'t '
                        + 'have one loaded.\n\n'
                        + 'Options:\n'
                        + '- Go to **Modeling Tasks → Run Frag. Sim.** '
                        + 'to start a fragment simulation\n'
                        + '- Or load a protein with existing data '
                        + '(e.g. SIRT1/4RMG)\n'
                        + '- Or if you already have compounds, try '
                        + '**"dock and score"** instead',
                };
            }
        }
    }

    // Compound loaded? (need at least one as the starting point for growing)
    if (typeof workspace.getLoadedCompounds === 'function') {
        const compounds = workspace.getLoadedCompounds();
        if (!Array.isArray(compounds) || compounds.length === 0) {
            return {
                ready: false,
                message: 'I understand you want to run **hit discovery**. '
                    + 'However, I need at least one compound as a '
                    + 'starting point for fragment growing. Load a '
                    + 'compound first (starter compounds, sketcher, '
                    + 'or import).',
            };
        }
    }

    return { ready: true };
}

// ─── Workflow builder ──────────────────────────────────────────────────────

/**
 * Build the fragment-based hit discovery workflow.
 * This is the REAL hit discovery pipeline:
 *
 *   1. grow-from-all-atoms — search every bond vector for fragment extensions
 *   2. energy-minimize — relax all grown compounds
 *   3. predict-gnina-scores — rescore with GNINA CNN (skipDocking: true)
 *
 * The result: new compounds ranked by predicted binding affinity.
 *
 * @param {string} [targetLabel='the active protein']
 * @returns {Object} workflow-intent plan
 */
export function buildFragmentHitDiscoveryWorkflow(
    targetLabel = 'the active protein',
) {
    return {
        intent: 'workflow',
        summary: `Fragment-based hit discovery against ${targetLabel}: `
            + 'grow fragments from all eligible bond vectors on the '
            + 'active compound, minimize the grown compounds, and '
            + 'rescore with GNINA CNN. New compounds will appear in '
            + 'the Compounds panel, ranked by binding score.',
        actions: [
            {
                actionId: 'grow-from-all-atoms',
                params: { maxSuggestions: 1 },
                resolvedInputs: { compounds: 'active' },
                step: 1,
            },
            {
                actionId: 'energy-minimize',
                params: { minimizationType: 'Rigid' },
                resolvedInputs: { compounds: 'all', caseData: 'current' },
                step: 2,
            },
            {
                actionId: 'predict-gnina-scores',
                params: { skipDocking: true },
                resolvedInputs: { compounds: 'all' },
                step: 3,
            },
        ],
    };
}

/**
 * Top-level entry point for the hit discovery shortcut.
 *
 * @param {string} userMessage
 * @param {Object} workspaceState
 * @returns {Object|null} workflow intent, chat message, or null
 */
export function tryHitDiscoveryShortcut(userMessage, workspaceState) {
    if (!matchHitDiscoveryIntent(userMessage)) return null;

    const check = checkHitDiscoveryPreconditions(workspaceState);
    if (!check.ready) {
        return {
            intent: 'chat',
            message: check.message,
        };
    }

    return buildFragmentHitDiscoveryWorkflow();
}
